/**
 * ArcadeEngineSection — the live "watch a real combo build" demo.
 * Scroll progress through the section drives a small state machine that
 * cycles PERFECT/GREAT/PERFECT/PERFECT/GOOD/MISS. No real audio, just the
 * visual story.
 *
 * Used to be 100+ lines inside HomePage; now isolated for a) testability
 * and b) so we can later swap the demo for a video or real interactive cell.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Reveal } from '@/components/Reveal';
import { SectionHeading } from '@/components/SectionHeading';
import { SpotlightCard } from '@/components/SpotlightCard';
import { ScrollTrigger } from '@/lib/gsap';
import { multiplier, multiplierLabel } from '@/lib/score';

type DemoGrade = 'perfect' | 'great' | 'good' | 'miss';

const SEQ: Array<{ g: DemoGrade; combo: number }> = [
  { g: 'perfect', combo: 1 },
  { g: 'great',   combo: 2 },
  { g: 'perfect', combo: 3 },
  { g: 'perfect', combo: 4 },
  { g: 'good',    combo: 5 },
  { g: 'miss',    combo: 0 },
];

const GRADE_COLOR: Record<DemoGrade, string> = {
  perfect: '#00f0ff',
  great:   '#aaff00',
  good:    '#d8d8e8',
  miss:    '#ff2e88',
};

export function HomeArcadeEngine(): JSX.Element {
  const sectionRef = useRef<HTMLElement>(null);
  const [demoCombo, setDemoCombo] = useState(0);
  const [grade, setGrade] = useState<DemoGrade | null>(null);

  useLayoutEffect(() => {
    if (sectionRef.current === null) return;
    const st = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: 'top 70%',
      end: 'bottom 30%',
      scrub: 0.5,
      onUpdate: (self) => {
        const idx = Math.min(SEQ.length - 1, Math.floor(self.progress * SEQ.length));
        const step = SEQ[idx]!;
        setGrade(step.g);
        setDemoCombo(step.combo);
      },
    });
    return () => st.kill();
  }, []);

  const m = multiplier(demoCombo);
  const comboColor = m === 5 ? 'text-mint-400' : m === 3 ? 'text-ember-400' : 'text-honey-400';
  const tierColor = m === 5 ? 'border-mint-400/60 text-mint-300'
    : m === 3 ? 'border-ember-400/60 text-ember-300'
    : 'border-honey-400/60 text-honey-300';

  return (
    <section ref={sectionRef} className="relative py-24 md:py-32 border-t border-honey-400/10 overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 md:px-8 w-full">
        <Reveal>
          <SectionHeading
            eyebrow="Arcade Engine · 街机引擎"
            title={<>滚动看 <span className="text-gradient-warm">PERFECT / GREAT / GOOD / MISS</span> 评分</>}
            subtitle="不是单调的对错，是有节奏的连击。错一次归零、连对加倍率，每一次提交都有真实的音效和画面反馈。"
          />
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.4fr,1fr]">
          {/* Live demo */}
          <Reveal delay={0.05}>
            <SpotlightCard spotlightColor="rgba(0, 240, 255, 0.32)" className="h-full">
              <div className="text-xs uppercase tracking-[0.2em] font-arcade text-honey-300 mb-4">
                LIVE DEMO · 30 秒看完
              </div>
              <div className="relative aspect-[5/3] rounded-2xl bg-ink-950 border border-honey-400/20 overflow-hidden scanlines">
                <div className="absolute inset-0 bg-dotmatrix opacity-30" />
                {/* Combo HUD */}
                <div className="absolute top-4 right-4 text-right z-10">
                  <motion.div
                    key={demoCombo}
                    initial={{ scale: 1.3 }}
                    animate={{ scale: 1 }}
                    className={`font-score text-5xl text-neon ${comboColor}`}
                  >
                    ×{demoCombo}
                  </motion.div>
                  <div className="text-[10px] font-arcade tracking-[0.2em] text-cream-300">COMBO</div>
                  {m > 1 && (
                    <div className={`mt-1 text-xs font-arcade px-2 py-1 rounded border inline-block ${tierColor}`}>
                      {multiplierLabel(m)}
                    </div>
                  )}
                </div>

                {/* Center grade burst */}
                <div className="absolute inset-0 grid place-items-center">
                  <AnimatePresence mode="wait">
                    {grade !== null && (
                      <motion.div
                        key={grade + demoCombo}
                        initial={{ scale: 0.5, opacity: 0, y: 30 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 1.3, opacity: 0, y: -20 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 18 }}
                        className="text-center"
                      >
                        <div
                          className="font-score text-6xl md:text-7xl text-neon text-chromatic"
                          style={{ color: GRADE_COLOR[grade] }}
                        >
                          {grade.toUpperCase()}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <div className="mt-3 text-xs text-cream-400 font-arcade text-center tracking-widest">
                ↑ 真实的 Combo / 评分 / 音效都在学习页里
              </div>
            </SpotlightCard>
          </Reveal>

          {/* Multiplier tiers + grading legend */}
          <div className="space-y-3">
            {(
              [
                ['×2', '4 连击 · STREAK',  'text-honey-300 border-honey-400/40'],
                ['×3', '8 连击 · ON FIRE', 'text-ember-300 border-ember-400/40 glow-pink'],
                ['×5', '16 连击 · LEGEND', 'text-mint-300 border-mint-400/40 glow-lime'],
              ] as const
            ).map(([mult, label, cls], i) => (
              <Reveal key={mult} delay={0.1 + i * 0.05}>
                <div className={`rounded-2xl border bg-ink-900/40 p-4 flex items-center gap-4 ${cls}`}>
                  <div className="font-score text-3xl">{mult}</div>
                  <div className="text-sm">{label}</div>
                  <div className="ml-auto text-xs text-cream-400 font-arcade">XP ×</div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.25}>
              <div className="rounded-2xl border border-cream-400/20 bg-ink-900/40 p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-cream-300 font-arcade mb-2">
                  评分依据
                </div>
                <ul className="text-xs text-cream-300/90 space-y-1">
                  <li>· 首次提交 + 4 秒内 + 0 错 → <span className="text-honey-300">PERFECT</span></li>
                  <li>· 首次提交 + 慢一点 → <span className="text-mint-300">GREAT</span></li>
                  <li>· 用了提示 / 修改过 → <span className="text-cream-200">GOOD</span></li>
                  <li>· 错了 → <span className="text-ember-300">MISS · 连击归零</span></li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
